import { CheckCircle2, X } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SEVERITY_META } from '@/features/schema/severity'
import type { ConceptDefinition, ConceptSeverity, ValidationFinding } from '@/services/api/schemas'
import { FindingRow } from './FindingRow'

const VISIBLE_SEVERITIES: ConceptSeverity[] = ['error', 'warning', 'hint']

export function FindingsPanel({
  findings,
  findingsBySeverity,
  conceptById,
  parseError,
  serverError,
  onDismiss,
}: {
  findings: ValidationFinding[] | null
  findingsBySeverity: Record<ConceptSeverity, ValidationFinding[]>
  conceptById: Record<string, ConceptDefinition>
  parseError: string | null
  serverError: string | null
  /** When set, renders a dismiss (×) button that clears the panel. */
  onDismiss?: () => void
}) {
  const { t } = useTranslation()
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="text-base">{t('schemaAgent.editor.findings.title')}</CardTitle>
        {onDismiss && (
          <CardAction>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onDismiss}
              className="text-muted-foreground hover:text-foreground"
              title={t('schemaAgent.editor.findings.dismiss')}
              aria-label={t('schemaAgent.editor.findings.dismiss')}
            >
              <X className="h-4 w-4" />
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {parseError && (
          <div className="border-destructive/50 bg-destructive/5 rounded-md border p-3 text-sm">
            <div className="text-destructive font-medium">
              {t('schemaAgent.editor.findings.invalidJson')}
            </div>
            <div className="text-muted-foreground mt-1 font-mono text-xs">{parseError}</div>
          </div>
        )}
        {serverError && (
          <div className="border-destructive/50 bg-destructive/5 rounded-md border p-3 text-sm">
            <div className="text-destructive font-medium">
              {t('schemaAgent.editor.findings.requestFailed')}
            </div>
            <div className="text-muted-foreground mt-1 text-xs">{serverError}</div>
          </div>
        )}
        {findings === null && !parseError && !serverError && (
          <div className="text-muted-foreground border-border bg-muted/30 rounded-md border border-dashed p-6 text-center text-sm">
            <Trans
              i18nKey="schemaAgent.editor.findings.prompt"
              components={{ strong: <span className="font-medium" /> }}
            />
          </div>
        )}
        {findings !== null && findings.length === 0 && !parseError && !serverError && (
          <div className="border-border bg-muted/30 flex items-center gap-2 rounded-md border p-3 text-sm">
            <CheckCircle2 className="text-foreground h-4 w-4" />
            <span>{t('schemaAgent.editor.findings.clean')}</span>
          </div>
        )}
        {findings !== null && findings.length > 0 && (
          <div className="space-y-3">
            {VISIBLE_SEVERITIES.map(sev =>
              findingsBySeverity[sev].length === 0 ? null : (
                <div key={sev} className="space-y-2">
                  <div className="text-muted-foreground flex items-center gap-2 text-xs font-semibold uppercase">
                    <span>{t(SEVERITY_META[sev].labelKey)}</span>
                    <span className="text-muted-foreground/70">
                      {findingsBySeverity[sev].length}
                    </span>
                  </div>
                  {findingsBySeverity[sev].map((f, i) => (
                    <FindingRow
                      key={`${f.concept_id}-${f.location}-${i}`}
                      finding={f}
                      concept={conceptById[f.concept_id]}
                    />
                  ))}
                </div>
              )
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
